import React from 'react';
import round from 'lodash/round';
import GeneralChart from '@/components/seo/GeneralChart';
import HideChart from '@/components/HideChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { JobTitleContext } from '@/context/create-context';
import { numberToUpper } from '@/utils/numbers';

const RecruitmentOverview = (props: {
  id: string;
  trendData: Record<string, any>;
}) => {
  const { id, trendData } = props;
  const {
    jobTitle: {
      job_open_details: {
        new_job_details: { by_month: newJobDetailsByMonth = [] },
      },
    },
    jobTitleSummary: {
      new_job_details: { by_month: newJobDetailsBySummary = [] },
    },
    displayName,
    isJobTitleCity,
  } = React.useContext(JobTitleContext);
  const lineChartRef = React.useRef(null);

  const lineConfig = React.useMemo<{
    config: ECOption;
    seoDetail: {
      isHide: boolean;
      currentData: { month: string; value: number };
      trendText: string;
      monthText: string;
    };
  }>(() => {
    const reGetGrowthRate = (value: any[]) => {
      let factor = 1;
      return value.map((item, index) => {
        if (index === 0) return 0;
        factor *= 1 + item?.growth_rate || 0;
        return round(factor - 1, 3);
      });
    };
    const jobDetailsByMonth = newJobDetailsByMonth.slice(-4);
    const JobDetailsBySummary = jobDetailsByMonth
      .map((item) =>
        newJobDetailsBySummary.find((sItem) => sItem.name === item.name),
      )
      .filter(Boolean);
    const jobDetailsByMonthGrowthRate = reGetGrowthRate(jobDetailsByMonth);
    const JobDetailsBySummaryGrowthRate = reGetGrowthRate(JobDetailsBySummary);
    const jobDetailsByMonthData = jobDetailsByMonth.map((item, index) => ({
      name: displayName,
      month: `${parseInt(item.name.split('-')[1])}月`,
      value: jobDetailsByMonthGrowthRate[index],
      count: item?.value,
    }));
    const jobDetailsBySummaryData = jobDetailsByMonth.map((item, index) => ({
      name: '全部职位',
      month: `${parseInt(item?.name.split('-')[1])}月`,
      value: JobDetailsBySummaryGrowthRate[index],
      count: item?.value,
    }));
    const totalGrowthRate = jobDetailsByMonthData
      .concat(jobDetailsBySummaryData)
      .map((item) => item?.value ?? 0);
    const max = Math.max(...totalGrowthRate);
    const min = Math.min(...totalGrowthRate);

    const jobDetailLength = jobDetailsByMonth.length;
    const growthRate =
      jobDetailsByMonthGrowthRate[jobDetailsByMonthGrowthRate.length - 1];
    const trendText = `近${numberToUpper(jobDetailLength)}月开放岗位${growthRate > 0 ? '上升' : '下降'}${Math.abs(round(growthRate * 100, 1))}%，${growthRate > 0 ? '需求紧俏，处于上升周期' : '需求放缓，处于下降周期'}`;

    const monthChange =
      jobDetailLength >= 2
        ? jobDetailsByMonth[jobDetailLength - 1].value -
          jobDetailsByMonth[jobDetailLength - 2].value
        : 0;
    const monthText = `对比上月：岗位${monthChange > 0 ? '新增' : '减少'}${Math.abs(monthChange)}`;
    return {
      config: {
        grid: {
          show: false,
          top: '20%',
          bottom: '8%',
          left: min >= 0 ? '10%' : '13%',
          right: 0,
        },
        xAxis: {
          type: 'category',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '6e6e6e', fontSize: 12 },
          data: jobDetailsBySummaryData.map((item) => item.month),
        },
        yAxis: {
          type: 'value',
          show: false,
          min: min - 0.06,
          max: max + 0.06,
        },
        legend: {
          show: true,
          type: 'plain',
          right: 0,
          top: '0.95%',
          icon: 'circle',
          itemHeight: 6,
          itemWidth: 6,
          itemGap: 8,
          selectedMode: false,
          textStyle: {
            padding: [0, 0, 0, 2],
            fontSize: 12,
            color: '#2d2d2d',
            backgroundColor: 'transparent',
          },
          formatter: (name) =>
            name.length > 25 ? name.substring(0, 25) + '...' : name,
        },
        series: [
          {
            name: displayName,
            type: 'line',
            zlevel: 1,
            data: jobDetailsByMonthData,
            smooth: true,
            cursor: 'default',
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#0050b3',
            },
            lineStyle: {
              width: 2,
            },
            emphasis: {
              scale: 1.4,
              itemStyle: {
                color: '#0050b3',
              },
              label: {
                show: true,
                fontSize: 12,
                color: '#2d2d2d',
                backgroundColor: 'transparent',
                formatter: ({ value }) =>
                  round((Number(value) || 0) * 100, 1) + '%',
              },
            },
          },
          {
            name: '全部职位',
            type: 'line',
            zlevel: 1,
            data: jobDetailsBySummaryData,
            smooth: true,
            cursor: 'default',
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#dbe2fd',
            },
            lineStyle: {
              width: 2,
            },
            emphasis: {
              scale: 1.4,
              itemStyle: {
                color: '#dbe2fd',
              },
              label: {
                show: true,
                fontSize: 12,
                color: '#2d2d2d',
                backgroundColor: 'transparent',
                formatter: ({ value }) =>
                  round((Number(value) || 0) * 100, 1) + '%',
              },
            },
            markLine: {
              silent: true,
              symbol: 'none',
              data: [
                {
                  yAxis: min,
                },
                {
                  yAxis: min + (max - min) / 3,
                },
                {
                  yAxis: min + ((max - min) * 2) / 3,
                },
                {
                  yAxis: max,
                },
              ],
              label: {
                position: 'start',
                fontSize: 12,
                distance: [4, 0],
                color: '#6e6e6e',
                formatter: ({ value }) => `${Math.floor(Number(value) * 100)}%`,
              },
              lineStyle: {
                color: '#f0f0f0',
                width: 1,
                type: 'solid',
              },
            },
          },
        ],
      },
      seoDetail: {
        currentData: {
          month: jobDetailsByMonthData[jobDetailLength - 1].month,
          value: jobDetailsByMonthData[jobDetailLength - 1].count,
        },
        isHide: jobDetailLength <= 1,
        trendText,
        monthText,
      },
    };
  }, [displayName, newJobDetailsByMonth, newJobDetailsBySummary]);

  return (
    <GeneralChart id={id} padding="p-0">
      <div className="grid grid-cols-2">
        <div className="pl-10 pr-6 pt-8 pb-4 border-r border-white-smoke flex flex-col">
          <h3 className="font-semibold">{`${lineConfig.seoDetail.currentData.month}新增岗位`}</h3>
          <p className="text-4.5xl font-semibold mt-2">
            {lineConfig.seoDetail.currentData.value}
          </p>
          <p className="text-sm my-2">{lineConfig.seoDetail.monthText || ''}</p>
          <p className="text-sm mt-4">
            {trendData[isJobTitleCity ? '城市趋势' : '全国趋势'] || ''}
          </p>
          <p className="text-xs text-web-grey-600 mt-auto">
            数据由各大平台公开数据统计分析而来，仅供参考。
          </p>
        </div>
        <div className="px-10 py-8">
          <HideChart
            isHide={lineConfig.seoDetail.isHide}
            hideTitle="岗位需求趋势"
          >
            <div className="h-56 relative [&_canvas]:cursor-default">
              <h3 className="font-semibold absolute top-0 left-0 z-1">
                岗位需求趋势
              </h3>
              {/*<p className="absolute top-8 text-sm !leading-5 z-1">*/}
              {/*  {lineConfig.seoDetail.trendText || ''}*/}
              {/*</p>*/}
              <CusEcharts chartRef={lineChartRef} config={lineConfig.config} />
            </div>
          </HideChart>
        </div>
      </div>
    </GeneralChart>
  );
};

export default RecruitmentOverview;
